/**
 * 阶段 3：三栏横屏实战战场布局与观战视角渲染器 (Battlefield Layout & Spectator View Manager)
 * 职责：
 * 1. 构建左栏友方微缩盘（1v1/无队友时自动切换为实时攻防战况仪表）与右栏敌方 1~3 微缩盘
 * 2. 观战模式下确保主棋盘聚焦选手不会重复出现在左上角队友栏，并支持点击任意小棋盘无缝切换观战主视角
 * 3. 使用 RivalTileRenderer 驱动观战主棋盘平滑滑动、合并缩放与对半劈砍特效
 * 4. 实时同步顶栏红蓝均分拉锯条与本地蓄能状态
 */

import type { TileData } from "../../shared/protocol";
import { LocalTileRenderer, RivalTileRenderer } from "../ui/pkRenderer";
import { ArenaDomRefs, escapeHtml } from "../ui/domRefs";
import type { ArenaRuntimeState } from "../state/arenaState";
import { getDodgeChanceByGrade } from "./dmcRankManager";

export class BattlefieldView {
  constructor(
    private readonly dom: ArenaDomRefs,
    private readonly state: ArenaRuntimeState,
    private readonly localRenderer: LocalTileRenderer,
    private readonly spectatorMainRenderer: RivalTileRenderer,
    private readonly primaryEnemyRenderer: RivalTileRenderer
  ) {}

  /**
   * 构建左栏(友方微缩盘)与右栏(敌方 1~3 微缩盘)的 DOM 及 RivalTileRenderer 实例
   */
  public setupBattlefieldBoards() {
    const mainBoardId = this.state.getMainBoardPlayerId();
    const mainParticipant = this.state.getMainBoardParticipant();
    const viewTeam = this.state.getViewTeam();
    const enemyTeam: "red" | "blue" = viewTeam === "red" ? "blue" : "red";

    // 主棋盘当前承载的选手直接绑定中央大棋盘 DOM，绝不重复挂载到左上角队友小棋盘
    if (mainParticipant) {
      mainParticipant.cardEl = undefined;
      mainParticipant.wrapperEl = this.dom.elLocalBoardWrapper;
      mainParticipant.renderer = undefined;
      mainParticipant.scoreEl = undefined;
      mainParticipant.statusEl = undefined;
    }

    const isSpectator = this.state.myTeam === "spectator";
    this.dom.elRivalTeamTag.textContent = enemyTeam === "red" ? "RED" : "BLUE";
    this.dom.elRivalTeamTag.style.background = enemyTeam === "red" ? "#e11d48" : "#0284c7";
    this.dom.elRivalRole.textContent = isSpectator
      ? enemyTeam === "red"
        ? "红方"
        : "蓝方"
      : "对手";

    const allies = Array.from(this.state.participants.values()).filter(
      (p) => p.team === viewTeam && p.id !== mainBoardId
    );
    const enemies = Array.from(this.state.participants.values()).filter(
      (p) => p.team === enemyTeam
    );

    // 1. 挂载左栏友方/其他视角微缩棋盘 (1v1 或无额外视角时展示实时攻防交锋战况仪表)
    this.dom.elAlliesMiniContainer.innerHTML = "";
    this.dom.elAlliesMiniContainer.classList.toggle("has-two-allies", allies.length >= 2);
    this.dom.elAlliesMiniContainer.classList.toggle("has-one-ally", allies.length === 1);
    const allyPanelTitle = this.dom.elAlliesMiniContainer
      .closest(".allies-panel")
      ?.querySelector(".sub-panel-title") as HTMLElement | null;
    const allyRescueHint = document.getElementById("ally-rescue-hint");

    if (allies.length === 0) {
      if (allyPanelTitle) allyPanelTitle.textContent = "本局战况";
      if (allyRescueHint) {
        allyRescueHint.textContent = isSpectator
          ? "OB 观战"
          : enemies.length > 1
            ? `1v${enemies.length}`
            : "1v1";
      }
      this.dom.elAlliesMiniContainer.innerHTML = `
        <div class="solo-telemetry-box">
          <div class="telemetry-stats-row">
            <div class="telemetry-pill">
              <span class="telemetry-val val-slash" id="live-stat-slash">${this.state.slashHits}</span>
              <span class="telemetry-lbl">劈砍</span>
            </div>
            <div class="telemetry-pill">
              <span class="telemetry-val val-drain" id="live-stat-drain">${this.state.scoreDrained}</span>
              <span class="telemetry-lbl">削分</span>
            </div>
            <div class="telemetry-pill">
              <span class="telemetry-val val-block" id="live-stat-block">${this.state.shieldBlocks}</span>
              <span class="telemetry-lbl">格挡</span>
            </div>
          </div>
        </div>
      `;
    } else {
      if (allyPanelTitle) {
        allyPanelTitle.textContent = isSpectator
          ? `其他视角 (${allies.length})`
          : `队友 (${allies.length})`;
      }
      if (allyRescueHint) {
        allyRescueHint.textContent = isSpectator ? "点击切换" : "封盘互传";
      }
      for (const ally of allies) {
        const card = document.createElement("div");
        card.className = "ally-board-card";
        card.setAttribute("data-player-id", ally.id);
        card.innerHTML = `
          <div class="enemy-card-top">
            <span class="enemy-card-name">${escapeHtml(ally.name)}</span>
            <span class="ally-rescue-tag" data-role="ally-status">${ally.score}分</span>
          </div>
          <div class="rival-mirror-wrapper">
            <div class="rival-grid-container">
              ${Array.from({ length: 16 })
                .map(() => `<div class="rival-grid-cell"></div>`)
                .join("")}
            </div>
            <div class="rival-tile-container"></div>
          </div>
        `;
        this.dom.elAlliesMiniContainer.appendChild(card);

        const wrapperEl = card.querySelector(".rival-mirror-wrapper") as HTMLElement;
        const tileContainer = card.querySelector(".rival-tile-container") as HTMLElement;
        const cells = Array.from(card.querySelectorAll(".rival-grid-cell")) as HTMLElement[];
        const renderer = new RivalTileRenderer(tileContainer, cells);

        ally.cardEl = card;
        ally.wrapperEl = wrapperEl;
        ally.statusEl = card.querySelector('[data-role="ally-status"]') as HTMLElement;
        ally.renderer = renderer;

        card.addEventListener("click", () => {
          if (this.state.myTeam === "spectator") {
            this.switchSpectatorFocus(ally.id);
          }
        });
      }
    }

    // 2. 挂载右栏敌方 1~3 人微缩棋盘
    this.dom.elExtraEnemiesSlot.innerHTML = "";
    const isMultiEnemies = enemies.length > 1;
    this.dom.elEnemiesMiniContainer.classList.toggle("multi-enemies", isMultiEnemies);
    this.dom.elEnemiesMiniContainer.classList.toggle("single-enemy", !isMultiEnemies);

    const firstEnemy = enemies[0];
    if (firstEnemy) {
      this.state.lockedEnemyId = firstEnemy.id;
      this.dom.elPrimaryEnemyCard.setAttribute("data-player-id", firstEnemy.id);
      this.dom.elPrimaryEnemyCard.classList.remove("active-target");
      this.dom.elPrimaryEnemyName.textContent = firstEnemy.name;
      const primaryLockEl = this.dom.elPrimaryEnemyCard.querySelector(
        ".enemy-card-lock"
      ) as HTMLElement | null;
      if (primaryLockEl) {
        primaryLockEl.textContent = `${firstEnemy.score}分`;
      }
      const enemySideLabel = enemyTeam === "red" ? "红方" : "蓝方";
      this.dom.elRivalName.textContent = isMultiEnemies
        ? isSpectator
          ? `${enemySideLabel} (${enemies.length}人)`
          : `敌方 (${enemies.length}人)`
        : firstEnemy.name;
      this.dom.elRivalStatusText.textContent = isSpectator
        ? "点击棋盘切换视角"
        : isMultiEnemies
          ? `全员 AOE · 最高合成: ${firstEnemy.maxTile}`
          : `最高合成: ${firstEnemy.maxTile}`;

      this.primaryEnemyRenderer.clearAll();
      this.primaryEnemyRenderer.updateCoordsCache();

      firstEnemy.cardEl = this.dom.elPrimaryEnemyCard;
      firstEnemy.wrapperEl = this.dom.elRivalMirrorWrapper;
      firstEnemy.scoreEl = primaryLockEl || undefined;
      firstEnemy.renderer = this.primaryEnemyRenderer;
    }

    for (let i = 1; i < enemies.length; i++) {
      const enemy = enemies[i];
      const card = document.createElement("div");
      card.className = "enemy-board-card";
      card.setAttribute("data-player-id", enemy.id);
      card.innerHTML = `
        <div class="enemy-card-top">
          <span class="enemy-card-name">${escapeHtml(enemy.name)}</span>
          <span class="enemy-card-lock" data-role="enemy-score">${enemy.score}分</span>
        </div>
        <div class="rival-mirror-wrapper">
          <div class="rival-grid-container">
            ${Array.from({ length: 16 })
              .map(() => `<div class="rival-grid-cell"></div>`)
              .join("")}
          </div>
          <div class="rival-tile-container"></div>
        </div>
      `;
      this.dom.elExtraEnemiesSlot.appendChild(card);

      const wrapperEl = card.querySelector(".rival-mirror-wrapper") as HTMLElement;
      const tileContainer = card.querySelector(".rival-tile-container") as HTMLElement;
      const cells = Array.from(card.querySelectorAll(".rival-grid-cell")) as HTMLElement[];
      const renderer = new RivalTileRenderer(tileContainer, cells);

      enemy.cardEl = card;
      enemy.wrapperEl = wrapperEl;
      enemy.scoreEl = card.querySelector('[data-role="enemy-score"]') as HTMLElement;
      enemy.renderer = renderer;

      card.addEventListener("click", () => {
        if (this.state.myTeam === "spectator") {
          this.switchSpectatorFocus(enemy.id);
        }
      });
    }

    requestAnimationFrame(() => {
      if (this.state.myTeam === "spectator") {
        this.spectatorMainRenderer.updateCoordsCache();
        if (mainParticipant && mainParticipant.tiles.length > 0) {
          this.spectatorMainRenderer.syncRivalTiles(mainParticipant.tiles);
        }
      } else {
        this.localRenderer.repositionAll(this.state.localTiles);
      }
      for (const p of this.state.participants.values()) {
        if (p.renderer) {
          p.renderer.updateCoordsCache();
          if (p.tiles.length > 0) {
            p.renderer.syncRivalTiles(p.tiles);
          }
        }
      }
    });
  }

  public setLockedEnemy(enemyId: string) {
    this.state.lockedEnemyId = enemyId;
    const enemy = this.state.participants.get(enemyId);
    if (enemy) {
      this.logCombat(`已锁定集火目标：${enemy.name}`);
    }
    this.dom.elEnemiesMiniContainer.querySelectorAll(".enemy-board-card").forEach((el) => {
      el.classList.toggle("active-target", el.getAttribute("data-player-id") === enemyId);
    });
  }

  public switchSpectatorFocus(playerId: string) {
    const p = this.state.participants.get(playerId);
    if (!p || this.state.spectatorFocusId === playerId) return;
    this.state.spectatorFocusId = playerId;
    this.dom.elLocalTeamTag.textContent = p.team === "red" ? "RED" : "BLUE";
    this.dom.elLocalTeamTag.style.background = p.team === "red" ? "#e11d48" : "#0284c7";
    this.dom.elLocalName.textContent = `观战: ${p.name}`;
    this.dom.elLocalScore.textContent = String(p.score);
    this.spectatorMainRenderer.clearAll();
    this.setupBattlefieldBoards();
    this.updateTeamScoresHUD();
    this.logCombat(`观战主视角已切换至：${p.name}`);
  }

  /**
   * 观战模式下将焦点选手的盘面实时渲染到中央大棋盘（带平滑滑动、合并缩放与对半劈砍特效）
   */
  public syncSpectatorMainBoard(tiles: TileData[]) {
    this.spectatorMainRenderer.syncRivalTiles(tiles);
  }

  public updateTeamScoresHUD() {
    const redAvg = this.state.getTeamAvgScore("red");
    const blueAvg = this.state.getTeamAvgScore("blue");
    this.dom.elHudRedScore.textContent = String(redAvg);
    this.dom.elHudBlueScore.textContent = String(blueAvg);

    const total = Math.max(1, redAvg + blueAvg);
    const redPct =
      redAvg === 0 && blueAvg === 0
        ? 50
        : Math.min(90, Math.max(10, Math.round((redAvg / total) * 100)));
    this.dom.elHudTeamTugRed.style.width = `${redPct}%`;

    const viewTeam = this.state.getViewTeam();
    const enemySide: "red" | "blue" = viewTeam === "red" ? "blue" : "red";
    this.state.rivalScore = this.state.getTeamAvgScore(enemySide);
    this.state.rivalMaxTile = this.state.getTeamMaxTile(enemySide);
    this.dom.elRivalScore.textContent = String(this.state.rivalScore);

    const enemies = Array.from(this.state.participants.values()).filter(
      (p) => p.team === enemySide
    );
    if (this.state.myTeam === "spectator" && enemies.length > 1) {
      this.dom.elRivalStatusText.textContent = "点击棋盘切换视角";
    } else if (enemies.length > 1) {
      this.dom.elRivalStatusText.textContent = `全员 AOE · 最高合成: ${this.state.rivalMaxTile}`;
    } else {
      this.dom.elRivalStatusText.textContent = `最高合成: ${this.state.rivalMaxTile}`;
    }

    const slashEl = document.getElementById("live-stat-slash");
    const drainEl = document.getElementById("live-stat-drain");
    const blockEl = document.getElementById("live-stat-block");
    if (slashEl) slashEl.textContent = `${this.state.slashHits}`;
    if (drainEl) drainEl.textContent = `${this.state.scoreDrained}`;
    if (blockEl) blockEl.textContent = `${this.state.shieldBlocks}`;
  }

  public updateLocalStatsUI() {
    this.dom.elLocalScore.textContent = String(this.state.localScore);
    this.dom.elLocalChargeFill.style.width = `${Math.min(100, this.state.attackGauge)}%`;

    const mySide = this.state.getViewTeam();
    const enemySide: "red" | "blue" = mySide === "red" ? "blue" : "red";
    const isCrit = this.state.isUnderdogCritAgainst(
      this.state.localScore,
      this.state.getTeamMaxScore(enemySide)
    );
    const me = this.state.participants.get(this.state.myPeerId);
    const dodgePct = Math.round(getDodgeChanceByGrade(me?.styleGrade || "D") * 100);
    const stateLabel = isCrit ? "暴击态" : "压制态";
    this.dom.elLocalChargeStatus.textContent =
      dodgePct > 0 ? `${stateLabel} · 闪避${dodgePct}%` : stateLabel;
    this.updateTeamScoresHUD();
  }

  public syncModePillUI() {
    if (!this.dom.elHudModePill) return;
    const isEndless = this.state.gameMode === "endless";
    const isSpeed = this.state.gameMode === "speed_2048";
    this.dom.elHudModePill.textContent = isEndless
      ? "无限模式"
      : isSpeed
        ? "极速 3:00"
        : "标准 8:00";
    this.dom.elHudModePill.classList.toggle("mode-endless", isEndless);
    this.dom.elHudModePill.classList.toggle("mode-speed", isSpeed);
  }

  public logCombat(msg: string) {
    this.dom.elCombatLog.innerHTML = `<span class="log-item">${msg}</span>`;
  }
}
